// AI assisted development
/**
 * Notification API client.
 */

import { getAccessToken, isAuthenticated } from './auth';
import type { DashboardNotification, NotificationListResponse } from '../types/notification';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://codereview-dev.azurewebsites.net/api';

class NotificationApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'NotificationApiError';
    this.status = status;
  }
}

async function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (options.headers) {
    const optHeaders = new Headers(options.headers);
    optHeaders.forEach((value, key) => {
      headers[key] = value;
    });
  }
  if (isAuthenticated()) {
    try {
      headers.Authorization = `Bearer ${await getAccessToken()}`;
    } catch (error) {
      console.error('Failed to get access token:', error);
    }
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'same-origin',
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new NotificationApiError(data.message || data.error || `HTTP ${response.status}`, response.status);
  }
  return response.json();
}

export const notificationApi = {
  list: (limit = 50): Promise<NotificationListResponse> =>
    fetchWithAuth<NotificationListResponse>(`/notifications?limit=${limit}`),

  markRead: (id: number): Promise<DashboardNotification> =>
    fetchWithAuth<DashboardNotification>(`/notifications/${id}/read`, { method: 'PATCH' }),

  markAllRead: (): Promise<{ updated: number }> =>
    fetchWithAuth<{ updated: number }>('/notifications/read-all', { method: 'POST' }),

  clearAll: (): Promise<{ deleted: number }> =>
    fetchWithAuth<{ deleted: number }>('/notifications/clear-all', { method: 'POST' }),
};
